import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, Video, ShieldCheck, Check, X, ExternalLink, Loader2 } from 'lucide-react';
import RaceAlertBlastsCard from './RaceAlertBlastsCard';
import GymPipelineCard from './GymPipelineCard';
import ListingApprovalLinksCard from './ListingApprovalLinksCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DirectoryTab() {
  const [status, setStatus] = useState('pending');
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async (st = status) => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/directory/submissions?status=${st}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` },
      });
      const data = await resp.json();
      setSubs(data.submissions || []);
      setCounts(data.counts || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(status); }, [status, load]);

  const review = async (id, action) => {
    setActing(id + action);
    try {
      await fetch(`${API_URL}/api/admin/directory/submissions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ action }),
      });
      await load(status);
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="directory-tab">
      <ListingApprovalLinksCard />
      <GymPipelineCard />
      <RaceAlertBlastsCard />
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-teal-500" />
          <h2 className="text-sm font-bold text-gray-900">HYROX Directories — Submissions</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Video submissions and listing claims from gym owners. Approving applies the change to the live directory page immediately.
        </p>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map((st) => (
            <button
              key={st}
              onClick={() => setStatus(st)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                status === st ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`directory-filter-${st}`}
            >
              {st} ({counts[st] ?? 0})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : subs.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-10" data-testid="directory-empty">No {status} submissions.</p>
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.submission_id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`directory-sub-${s.submission_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {s.type === 'video' ? <Video className="w-3.5 h-3.5 text-sky-500" /> : <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />}
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{s.type === 'video' ? 'Video' : 'Listing claim'}</span>
                    <span className="text-sm font-semibold text-gray-900">{s.gym_name}</span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5">
                    <p>From: {s.submitter_name || s.contact_name} · {s.submitter_email || s.contact_email}{s.phone ? ` · ${s.phone}` : ''}</p>
                    {s.video_url && (
                      <p>
                        Video: <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-0.5">{s.video_url.slice(0, 60)}… <ExternalLink className="w-3 h-3" /></a>
                      </p>
                    )}
                    {s.title && <p>Title: {s.title}</p>}
                    {s.buy_class_url && (
                      <p>
                        Buy link: <a href={s.buy_class_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-0.5">{s.buy_class_url.slice(0, 60)}… <ExternalLink className="w-3 h-3" /></a>
                      </p>
                    )}
                    {s.pricing_text && <p>Pricing: {s.pricing_text}</p>}
                    {s.notes && <p>Notes: {s.notes}</p>}
                    <p className="text-gray-400">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {s.status === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => review(s.submission_id, 'approve')}
                      disabled={!!acting}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid={`directory-approve-${s.submission_id}`}
                    >
                      {acting === s.submission_id + 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                    </button>
                    <button
                      onClick={() => review(s.submission_id, 'reject')}
                      disabled={!!acting}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-red-50 hover:text-red-600 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid={`directory-reject-${s.submission_id}`}
                    >
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
